import { useEffect, type RefObject } from 'react'

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface ModalBehaviourOptions {
  open: boolean
  /** The portal container holding the dialog. Everything else in <body> is made inert. */
  containerRef: RefObject<HTMLElement>
  dialogRef: RefObject<HTMLElement>
  /** Element focused when the dialog opens. */
  initialFocusRef: RefObject<HTMLElement>
  /** Element focused again (without scrolling) after the dialog closes. */
  returnFocusTo: HTMLElement | null
  onClose: () => void
}

/**
 * Modal mechanics: inert background, iOS-safe body scroll lock that restores the
 * exact scroll position, initial focus, Tab trap, Escape, and focus restoration.
 */
export function useModalBehaviour({
  open,
  containerRef,
  dialogRef,
  initialFocusRef,
  returnFocusTo,
  onClose,
}: ModalBehaviourOptions) {
  // Inert background + scroll lock + initial focus + focus return.
  useEffect(() => {
    if (!open) return undefined
    const body = document.body
    const scrollY = window.scrollY
    const previous = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    }
    // position:fixed is the lock iOS Safari respects; offset keeps the page visually still.
    body.style.position = 'fixed'
    body.style.top = `-${scrollY}px`
    body.style.left = '0'
    body.style.right = '0'
    body.style.width = '100%'
    body.style.overflow = 'hidden'

    const made: HTMLElement[] = []
    Array.from(body.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return
      if (child === containerRef.current) return
      if (child.hasAttribute('inert')) return
      child.setAttribute('inert', '')
      child.setAttribute('aria-hidden', 'true')
      made.push(child)
    })

    initialFocusRef.current?.focus({ preventScroll: true })

    const returnTarget = returnFocusTo
    return () => {
      made.forEach((el) => {
        el.removeAttribute('inert')
        el.removeAttribute('aria-hidden')
      })
      body.style.position = previous.position
      body.style.top = previous.top
      body.style.left = previous.left
      body.style.right = previous.right
      body.style.width = previous.width
      body.style.overflow = previous.overflow
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' as ScrollBehavior })
      if (returnTarget && returnTarget.isConnected) returnTarget.focus({ preventScroll: true })
    }
    // returnFocusTo is captured at open time on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Escape and Tab trap, handled at document level so they work even if focus drifts.
  useEffect(() => {
    if (!open) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const dialog = dialogRef.current
      const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) {
        event.preventDefault()
        initialFocusRef.current?.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (!dialog.contains(active)) {
        event.preventDefault()
        first.focus()
      } else if (event.shiftKey && (active === first || active === initialFocusRef.current)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, dialogRef, initialFocusRef])
}
